import type { RowValue } from "../../../../../../lib/csv";
import type { Options } from "../../../validatorCSV";
import {
  validatorDataNotPresent,
  validatorMultipleDataNotPresent,
  validatorWrongFormatData,
} from "../../../validatorCSV";
import type { FixedColumn } from "./FilesType";
import { FAILURE_PARSING_ERROR } from "./FilesType";
import type { Reparability } from "../../../../../../api/Reparability";
import { reparabilities } from "../../../../../../api/Reparability";

export const MAX_FAULT_NAME_LENGTH = 100;
export const MAX_FAULT_TECHNICAL_NAME_LENGTH = 100;
export const MAX_FAULT_DISPLAY_NAME_LENGTH = 100;
export const MAX_FAULT_UNIT_LENGTH = 100;

//? Validator
export const validatorDuplicationFilterFormula = (content: RowValue[]) =>
  content.filter(row => {
    const regexFormula = /^filterFormula(\d+)$/;
    const filtersByTypeAndQuantity: Record<string, Set<string>> = {};

    for (const key of Object.keys(row)) {
      const match = regexFormula.exec(key);
      if (match) {
        const filterIndex = match[1];
        const formulaValue = row[key]?.trim();
        const filterTypeKey = `filterType${filterIndex}`;
        const filterType = row[filterTypeKey]?.trim() ?? "CONSTRAINT";

        if (!formulaValue) continue;
        if (!filtersByTypeAndQuantity[filterType]) filtersByTypeAndQuantity[filterType] = new Set();
        if (filtersByTypeAndQuantity[filterType].has(formulaValue)) return true;

        filtersByTypeAndQuantity[filterType].add(formulaValue);
      }
    }

    return false;
  });

export const validatorFilterFormulaFunction = (
  content: RowValue[],
  regex: RegExp,
  filterFn: (match: RegExpExecArray | null) => boolean,
) =>
  content.filter(row => {
    const regexKey = /^filterFormula\d+$/;
    for (const key of Object.keys(row)) {
      if (regexKey.test(key)) {
        const value = row[key];
        if (value) {
          const match = regex.exec(value);
          if (filterFn(match)) return true;
        }
      }
    }
    return false;
  });

function validatorDependancyFilterFormulaFunction(content: RowValue[]) {
  return content.filter(row => {
    const regexKey = /^filterFormula(\d+)$/;
    for (const key of Object.keys(row)) {
      const match = regexKey.exec(key);
      if (match && row[key]) {
        const filterNumber = match?.[1];
        const minKey = `filterMin${filterNumber}`;
        const maxKey = `filterMax${filterNumber}`;
        const min = row[minKey];
        const max = row[maxKey];
        if (!min && !max) return true;
      }
    }
    return false;
  });
}

function validatorDependancyFilterFunction(content: RowValue[], regexFilter: RegExp) {
  return content.filter(row => {
    const filterNumbers = Object.keys(row)
      .filter(key => regexFilter.test(key) && row[key] !== null)
      .map(key => regexFilter.exec(key)?.[1])
      .filter(Boolean);

    if (filterNumbers.length === 0) return false;

    return !filterNumbers.every(filterNumber => {
      const regexString = `^filterFormula${filterNumber}$`;
      const regexFormulaNumber = new RegExp(regexString);
      return Object.keys(row).some(key => regexFormulaNumber.test(key) && row[key] !== null);
    });
  });
}

const extractFilterValues = (row: RowValue, regex: RegExp) =>
  Object.keys(row).flatMap(key => {
    const regexMatch = key.match(regex);
    if (regexMatch === null) return [];

    const filterNumber = regexMatch[1];
    if (filterNumber === null) return [];
    return { index: Number(filterNumber), value: row[key] ?? null };
  });
const validatorFilterMinGreaterThanFilterMax = (content: RowValue[]) =>
  content.filter(row => {
    const regexMin = /^filterMin(\d+)$/;
    const regexMax = /^filterMax(\d+)$/;

    const filtersMin = extractFilterValues(row, regexMin);
    const filtersMax = extractFilterValues(row, regexMax);

    if (filtersMin.length !== filtersMax.length) return true;
    if (filtersMin.length === 0 || filtersMax.length === 0) return false;

    let areAllConstraintsValid = true;
    for (const { index, value: min } of filtersMin) {
      const max = filtersMax.find(e => e.index === index)?.value ?? null;
      if (min === null || max === null) continue;
      if (parseFloat(min) > parseFloat(max)) {
        areAllConstraintsValid = false;
        break;
      }
    }

    return !areAllConstraintsValid;
  });

const validatorMinMaxNotNumber = (content: RowValue[]) =>
  content.filter(row => {
    const regexMinMax = /^filter(Min|Max)\d+$/;
    for (const key of Object.keys(row)) {
      const minMaxFilter = key in row ? row[key] : null;
      if (regexMinMax.test(key) && minMaxFilter !== null && isNaN(Number(minMaxFilter))) {
        return true;
      }
    }
    return false;
  });

const validatorTechnicalNameTooLong = (content: RowValue[]) =>
  content.filter(e => {
    const technicalName = e["technicalName"];
    return (
      typeof technicalName === "string" && technicalName.length > MAX_FAULT_TECHNICAL_NAME_LENGTH
    );
  });

const validatorDisplayNameTooLong = (content: RowValue[]) =>
  content.filter(e => {
    const displayName = e["displayName"];
    return typeof displayName === "string" && displayName.length > MAX_FAULT_DISPLAY_NAME_LENGTH;
  });

const validatorTechnicalNameDuplicated = (content: RowValue[]) =>
  content.filter((row, i, arr) => {
    const current = row["technicalName"];
    return typeof current === "string" && i !== arr.findIndex(r => r["technicalName"] === current);
  });

const validatorDisplayNameDuplicated = (content: RowValue[]) => {
  const groups = new Map<string, string[]>();

  for (const row of content) {
    const displayName = row["displayName"];
    const technicalName = row["technicalName"];
    if (typeof displayName === "string" && typeof technicalName === "string") {
      groups.set(displayName, [...(groups.get(displayName) ?? []), technicalName]);
    }
  }

  const duplicatedTechnicalNames = new Set(
    Array.from(groups.values())
      .filter(list => list.length > 1)
      .flat(),
  );

  return content.filter(row => {
    const technicalName = row["technicalName"];
    return typeof technicalName === "string" && duplicatedTechnicalNames.has(technicalName);
  });
};

export const validatorDisplayNameEmpty = (content: RowValue[]) => {
  const isDisplayNameColumnPresent = content.some(row => "displayName" in row);

  if (!isDisplayNameColumnPresent) return [];

  return content.filter(row => {
    const displayName = row["displayName"];
    return displayName === undefined || displayName === null || displayName.trim() === "";
  });
};

export const validatorTechnicalNameEmpty = (content: RowValue[]) => {
  const isTechnicalNameColumnPresent = content.some(row => "technicalName" in row);
  if (!isTechnicalNameColumnPresent) return [];

  return content.flatMap((row, index) => {
    const technicalName = row["technicalName"];
    if (!technicalName || technicalName.trim() === "") {
      return [{ ...row, line: `${index + 2}` }];
    }
    return [];
  });
};

const validatorUnitSize = (content: RowValue[]) =>
  content.filter(e => {
    const unit = e["unit"];
    if (typeof unit === "string") return unit.length > MAX_FAULT_UNIT_LENGTH;
    return false;
  });

const validatorMagnitudeNotANumber = (content: RowValue[]) =>
  content.filter(e => {
    const magnitude = e["magnitude"];
    return magnitude && (Number.isNaN(parseFloat(magnitude)) || parseFloat(magnitude) === 0);
  });

const validatorMaxNotANumber = (content: RowValue[]) =>
  content.filter(e => {
    const max = e["maximumValue"];
    return max ? Number.isNaN(parseFloat(max)) : true;
  });

const validatorMinNotANumber = (content: RowValue[]) =>
  content.filter(e => {
    const min = e["minimumValue"];
    return min ? Number.isNaN(parseFloat(min)) : true;
  });

const validatorMinGreaterThanMax = (content: RowValue[]) =>
  content.filter(e => {
    const min = e["minimumValue"];
    const max = e["maximumValue"];
    return min && max && Number(min) >= Number(max);
  });

const validatorReparabilityNotValid = (content: RowValue[]) =>
  content.filter(e => {
    const reparability = e["reparability"];
    return reparability && !reparabilities.includes(reparability as Reparability);
  });

const validatorActivatedNotValid = (content: RowValue[]) =>
  content.filter(e => {
    const activated = e["activated"];
    return activated && activated !== "0" && activated !== "1";
  });

//? Settings
export const options: Partial<Options<FixedColumn>> = {
  technicalName: {
    format: [
      {
        validator: validatorTechnicalNameTooLong,
        message: FAILURE_PARSING_ERROR.TECHNICAL_NAME_TOO_LONG,
      },
      {
        validator: validatorTechnicalNameDuplicated,
        message: FAILURE_PARSING_ERROR.DUPLICATED_TECHNICAL_NAME,
      },
      {
        validator: validatorTechnicalNameEmpty,
        message: FAILURE_PARSING_ERROR.TECHNICAL_NAME_EMPTY,
      },
    ],
  },
  displayName: {
    format: [
      {
        validator: validatorDisplayNameTooLong,
        message: FAILURE_PARSING_ERROR.DISPLAY_NAME_TOO_LONG,
      },
      {
        validator: validatorDisplayNameDuplicated,
        message: FAILURE_PARSING_ERROR.DUPLICATED_DISPLAY_NAME,
      },
      {
        validator: validatorDisplayNameEmpty,
        message: FAILURE_PARSING_ERROR.DISPLAY_NAME_EMPTY,
      },
    ],
  },

  activated: {
    format: [
      {
        validator: validatorActivatedNotValid,
        message: FAILURE_PARSING_ERROR.ACTIVATED_INVALID,
      },
    ],
  },
  maximumValue: {
    format: [
      {
        validator: validatorMaxNotANumber,
        message: FAILURE_PARSING_ERROR.MAX_NOT_A_NUMBER,
      },
    ],
  },
  minimumValue: {
    format: [
      {
        validator: validatorMinGreaterThanMax,
        message: FAILURE_PARSING_ERROR.MIN_GREATER_THAN_MAX,
      },
      {
        validator: validatorMinNotANumber,
        message: FAILURE_PARSING_ERROR.MIN_NOT_A_NUMBER,
      },
    ],
  },
  magnitude: {
    format: [
      {
        validator: validatorMagnitudeNotANumber,
        message: FAILURE_PARSING_ERROR.MAGNITUDE_NOT_NUMBER,
      },
    ],
  },
  reparability: {
    format: [
      {
        validator: validatorReparabilityNotValid,
        message: FAILURE_PARSING_ERROR.WRONG_INPUT_REPARABILITY,
      },
    ],
  },
  system: {
    permission: [
      {
        validator: (content: RowValue[], data: string[]) =>
          validatorDataNotPresent(content, data, "system"),
        message: FAILURE_PARSING_ERROR.MISSING_SYSTEM_CATEGORIES,
      },
    ],
  },
  equipmentType: {
    permission: [
      {
        validator: (content: RowValue[], data: string[]) =>
          validatorDataNotPresent(content, data, "equipmentType"),
        message: FAILURE_PARSING_ERROR.MISSING_EQUIPMENT_TYPE_CATEGORIES,
      },
    ],
  },
  degradationType: {
    permission: [
      {
        validator: (content: RowValue[], data: string[]) =>
          validatorDataNotPresent(content, data, "degradationType"),
        message: FAILURE_PARSING_ERROR.MISSING_DEGRADATION_TYPE_CATEGORIES,
      },
    ],
  },
  failureCategory: {
    permission: [
      {
        validator: (content: RowValue[], data: string[]) =>
          validatorDataNotPresent(content, data, "failureCategory"),
        message: FAILURE_PARSING_ERROR.MISSING_FAILURE_CATEGORIES,
      },
    ],
  },
  equipments: {
    format: [
      {
        validator: (content: RowValue[]) => validatorWrongFormatData(content, "equipments"),
        message: FAILURE_PARSING_ERROR.WRONG_INPUT_MULTI_VALUE,
      },
    ],
    permission: [
      {
        validator: (content: RowValue[], data: string[]) =>
          validatorMultipleDataNotPresent(content, data, "equipments"),
        message: FAILURE_PARSING_ERROR.MISSING_EQUIPMENTS_CATEGORIES,
        multiple: true,
      },
    ],
  },
  suspectedElements: {
    format: [
      {
        validator: (content: RowValue[], column = "suspectedElements") =>
          validatorWrongFormatData(content, column),
        message: FAILURE_PARSING_ERROR.WRONG_INPUT_MULTI_VALUE,
      },
    ],
    permission: [
      {
        validator: (content: RowValue[], data: string[], column = "suspectedElements") =>
          validatorMultipleDataNotPresent(content, data, column),
        message: FAILURE_PARSING_ERROR.MISSING_SUSPECTED_ELEMENTS_CATEGORIES,
        multiple: true,
      },
    ],
  },
  "filterFormula*": {
    format: [
      {
        validator: (content: RowValue[]) =>
          validatorFilterFormulaFunction(content, /^MEAN\((.+)\)$/, match => !match),
        message: FAILURE_PARSING_ERROR.WRONG_FORMAT_FILTER_FORMULA,
      },
      {
        validator: (content: RowValue[]) => validatorDependancyFilterFormulaFunction(content),
        message: FAILURE_PARSING_ERROR.MISSING_FILTER_FOMULA_DEPENDENCIED,
      },
      {
        validator: (content: RowValue[]) => validatorDuplicationFilterFormula(content),
        message: FAILURE_PARSING_ERROR.WRONG_DUPLICATION_FILTER_FOMULA,
      },
    ],
  },
  "filterMin*": {
    format: [
      {
        validator: (content: RowValue[]) =>
          validatorDependancyFilterFunction(content, /^filterMin(\d+)$/),
        message: FAILURE_PARSING_ERROR.MISSING_FILTER_MIN_DEPENDENCIES,
      },
      {
        validator: (content: RowValue[]) => validatorFilterMinGreaterThanFilterMax(content),
        message: FAILURE_PARSING_ERROR.FILTER_MIN_MUST_BE_LESS_THAN_FILTER_MAX,
      },
      {
        validator: (content: RowValue[]) => validatorMinMaxNotNumber(content),
        message: FAILURE_PARSING_ERROR.FILTER_MIN_MAX_MUST_BE_NUMBER,
      },
    ],
  },
  "filterMax*": {
    format: [
      {
        validator: (content: RowValue[]) =>
          validatorDependancyFilterFunction(content, /^filterMax(\d+)$/),
        message: FAILURE_PARSING_ERROR.MISSING_FILTER_MAX_DEPENDENCIES,
      },
    ],
  },
  unit: {
    format: [
      {
        validator: validatorUnitSize,
        message: FAILURE_PARSING_ERROR.UNIT_TOO_LONG,
      },
    ],
  },
};
